import { MAX_FILE_SIZE } from '../../../constants/statics'
import { MimeType } from '../../../constants/fileTypes'
import React, { Component } from 'react'
import _ from 'lodash'
import mime from 'mime-types'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import Dropzone from 'react-dropzone'
import Pretty from 'prettysize'
import classnames from 'classnames/bind'
import Icon from '../../elementals/icon'
import Text from '../../elementals/text'
import DownloadLink from '../../downloadLink'
import styles from './styles.scss'

const cx = classnames.bind(styles)

/**
 * Formats accepted MIME types into a user-friendly string
 * @param {string[]} acceptedFileTypes - Array of MIME types
 * @returns {string} Formatted string (e.g., '(PDF, JPEG, PNG)')
 */
const formatAcceptedFileTypes = acceptedFileTypes => {
  if (!acceptedFileTypes || acceptedFileTypes.length === 0) {
    return ''
  }

  const extensions = acceptedFileTypes
    .map(mimeType => {
      const ext = mime.extension(mimeType)
      return ext ? ext.toUpperCase() : null
    })
    .filter(Boolean)

  if (extensions.length === 0) {
    return ''
  }

  return ` (${extensions.join(', ')})`
}

export const PDF = MimeType.PDF
export const ERROR_MESSAGE_FILE_REQUIRED = 'File is required.'
export const ERROR_MESSAGE_FILE_TYPE_PDF_ONLY = 'Only pdf files accepted.'
export const ERROR_MESSAGE_FILE_TYPE_NOT_ACCEPTED = 'File type not accepted.'
export const ERROR_MESSAGE_TOO_MANY_FILES = maxFiles =>
  `Too many files specified.  You are limited to ${maxFiles} ${pluralize(
    'file',
    maxFiles
  )}.`
export const ERROR_MESSAGE_FILE_SIZE_LIMIT_REACHED = maxFileSize =>
  `File size is limited to ${Pretty(maxFileSize)}.`
export const ERROR_MESSAGE_DUPLICATE_FILE_NAMES = fileNames => {
  return `You already have a file name:\n ${fileNames.join('\n')}`
}
class FileDropzone extends Component {
  static propTypes = {
    files: PropTypes.array.isRequired,
    maxFileSize: PropTypes.number.isRequired,
    maxNumberOfFiles: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
    remoteError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    errorMessageFileRequired: PropTypes.string,
    errorMessageFileTypePDFOnly: PropTypes.string,
    errorMessageFileTypeNotAccepted: PropTypes.string,
    errorMessageFileSizeLimitReached: PropTypes.func,
    errorMessageTooManyFiles: PropTypes.func,
    errorMessageDuplicateFileNames: PropTypes.func,
    disabled: PropTypes.bool,
    compact: PropTypes.bool,
    multiple: PropTypes.bool,
    required: PropTypes.bool,
    className: PropTypes.string,
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        urn: PropTypes.string,
      })
    ),
    embeddedContext: PropTypes.string,
  }

  static defaultProps = {
    acceptedFileTypes: [PDF],
    maxFileSize: MAX_FILE_SIZE,
    maxNumberOfFiles: undefined,
    remoteError: false,
    errorMessageFileRequired: ERROR_MESSAGE_FILE_REQUIRED,
    errorMessageFileTypePDFOnly: ERROR_MESSAGE_FILE_TYPE_PDF_ONLY,
    errorMessageFileTypeNotAccepted: ERROR_MESSAGE_FILE_TYPE_NOT_ACCEPTED,
    errorMessageFileSizeLimitReached: ERROR_MESSAGE_FILE_SIZE_LIMIT_REACHED,
    errorMessageTooManyFiles: ERROR_MESSAGE_TOO_MANY_FILES,
    errorMessageDuplicateFileNames: ERROR_MESSAGE_DUPLICATE_FILE_NAMES,
    disabled: false,
    compact: false,
    multiple: false,
    required: false,
    embeddedContext: 'browser',
  }

  state = {
    error: null,
    files: this.props.files,
    dropzoneActive: false,
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let files = prevState.files
    if (!_.isEqual(nextProps.files, prevState.files)) files = nextProps.files

    return {
      files,
    }
  }

  componentDidUpdate() {
    // if file list has been modified from outside the component, the files state should reset locally
    const { files } = this.props
    if (!_.isEqual(files, this.state.files)) {
      this.setState({ files })
    }
  }

  onDrop = (filesToUpload, rejectedFiles, e) => {
    const {
      onChange,
      acceptedFileTypes,
      maxFileSize,
      maxNumberOfFiles,
      multiple,
      errorMessageFileTypePDFOnly,
      errorMessageFileTypeNotAccepted,
      errorMessageFileSizeLimitReached,
      errorMessageTooManyFiles,
      errorMessageDuplicateFileNames,
    } = this.props
    // We manually tell dropzone to do the onDragLeave command because it sometimes won't fire
    this.onDragLeave()

    if (!filesToUpload.every(file => acceptedFileTypes.includes(file.type))) {
      const error = _.isEqual(acceptedFileTypes, [PDF])
        ? errorMessageFileTypePDFOnly
        : errorMessageFileTypeNotAccepted
      this.setState({ error })
      return
    }

    if (filesToUpload.some(file => file.size > maxFileSize)) {
      this.setState({ error: errorMessageFileSizeLimitReached(maxFileSize) })
      return
    }

    let filesUploaded = this.state.files.slice() || []
    const uploadedFileNames = filesUploaded.map(file => file.name)
    const toUploadFileNames = filesToUpload.map(file => file.name)
    const duplicateFileNames = _.intersection(
      uploadedFileNames,
      toUploadFileNames
    )

    if (!_.isEmpty(duplicateFileNames)) {
      this.setState({
        error: errorMessageDuplicateFileNames(duplicateFileNames),
      })
      return
    }

    if (multiple) {
      filesUploaded.push(...filesToUpload)
      if (maxNumberOfFiles && filesUploaded.length > maxNumberOfFiles) {
        this.setState({ error: errorMessageTooManyFiles(maxNumberOfFiles) })
        return
      }
    } else {
      // In case dropzone doesn't have multiple prop and you try to drag and drop
      // more than one file, all files will be rejected and you'll have an empty array
      // In this case we keep the previous existing file
      filesUploaded = !_.isEmpty(filesToUpload) ? filesToUpload : filesUploaded
    }
    this.setState({ error: null, files: filesUploaded })
    // Do something with the file
    onChange(filesUploaded)
  }

  onDragEnter = () => this.setState({ dropzoneActive: true })

  onDragLeave = () => this.setState({ dropzoneActive: false })

  isBlob = file => _.startsWith(file.preview, 'blob:')

  renderDroppedFiles = files => {
    const {
      onChange,
      disabled,
      required,
      errorMessageFileRequired,
      embeddedContext,
      resources,
    } = this.props

    const isOnWebView = embeddedContext === 'webview'

    return (
      files.length > 0 &&
      Array.isArray(files) && (
        <ul className={cx('uploaded-files')}>
          {files.map((file, i) => {
            return (
              <li key={i}>
                {isOnWebView && resources?.[i] ? (
                  <div
                    className={cx('file-name')}
                    data-elm-id={`dropzone_input_file_name_${i}`}
                  >
                    <DownloadLink
                      embeddedContext={embeddedContext}
                      resource={resources[i]}
                    >
                      {file.name}
                    </DownloadLink>
                  </div>
                ) : (
                  <a
                    href={file.preview}
                    className={cx('file-name')}
                    data-elm-id={`dropzone_input_file_name_${i}`}
                    download={this.isBlob(file)}
                  >
                    {file.name}
                  </a>
                )}
                <Icon
                  className={cx(
                    'dropped-file-icons',
                    disabled && 'dropzone-disabled'
                  )}
                  color='gray'
                  iconStyle='regular'
                  name='trash'
                  size='medium'
                  elmId={`dropzone_input_remove_icon_${i}`}
                  onClick={() => {
                    if (disabled) return
                    const newFiles = files.slice()
                    newFiles.splice(i, 1)
                    onChange(newFiles, i)
                    const errorMsg =
                      required && files.length === 0
                        ? errorMessageFileRequired
                        : null
                    this.setState({ error: errorMsg, files: newFiles })
                  }}
                />
              </li>
            )
          })}
        </ul>
      )
    )
  }

  getIconColor = () => {
    const { disabled, remoteError } = this.props
    const { error, dropzoneActive } = this.state
    if (disabled) {
      return 'grayMedium'
    } else if (dropzoneActive) {
      return 'info'
    } else if (error || remoteError) {
      return 'error'
    } else {
      return 'black'
    }
  }

  render() {
    const {
      remoteError,
      disabled,
      label, // eslint-disable-line
      errorMessageFileRequired,
      errorMessageFileTypePDFOnly,
      errorMessageFileTypeNotAccepted,
      errorMessageFileSizeLimitReached,
      errorMessageTooManyFiles,
      errorMessageDuplicateFileNames,
      onChange,
      acceptedFileTypes,
      maxNumberOfFiles,
      maxFileSize,
      compact,
      className: customClassName,
      ...dropzoneProps
    } = this.props
    const { error, dropzoneActive, files } = this.state

    return (
      <div>
        {this.renderDroppedFiles(files)}
        <Dropzone
          className={cx(
            'dropzone',
            dropzoneActive ? 'dropzone-active' : '',
            error || remoteError ? 'dropzone-error' : '',
            disabled ? 'dropzone-disabled' : '',
            compact ? 'compact' : '',
            customClassName
          )}
          activeStyle={{
            // #008cbf is $utk-color-info
            border: '2px dashed #008cbf',
          }}
          disabled={disabled}
          onDrop={this.onDrop}
          onDragEnter={this.onDragEnter}
          onDragLeave={this.onDragLeave}
          inputProps={{
            'data-elm-id': 'dropzone_input',
          }}
          {...dropzoneProps}
        >
          <div className={cx('dropzone-text')}>
            <Icon
              color='grayMediumLight'
              iconStyle='regular'
              name='cloud-arrow-up'
              size='big'
            />
            <Text as='span' color='gray' size='tiny' weight='black'>
              Choose a file
              <Text
                as='span'
                className={cx('dropzone-or')}
                color='gray'
                size='tiny'
              >
                {' or drag it here'}
              </Text>
              <Text as='span' color='gray' size='tiny'>
                {formatAcceptedFileTypes(acceptedFileTypes)}
              </Text>
            </Text>
          </div>
        </Dropzone>
        {(error || remoteError) && (
          <div
            className={cx('dropzone-error-message')}
            data-elm-id='dropzone_error_message'
          >
            {error || remoteError}
          </div>
        )}
      </div>
    )
  }
}

export default FileDropzone
